import type { DashboardData, MultiRetailerOverviewData } from "../types";
import { log } from "../utils/logger";
import { buildApiUrl, getHttpStatusMessage } from "./transport";

/**
 * Fetch dashboard data from the backend.
 * No fallback - throws error if API fails.
 */
export async function fetchDashboardData(): Promise<DashboardData> {
  const url = buildApiUrl("/dashboard");

  log.info("Fetching dashboard data from API", { url }, "api");
  const response = await fetch(url);

  if (!response.ok) {
    const errorDetails = getHttpStatusMessage(response);
    log.error(
      "Dashboard data request failed",
      { status: response.status, errorDetails },
      "api",
    );
    throw new Error(errorDetails);
  }

  const data: DashboardData = await response.json();

  log.info(
    "Dashboard data fetched successfully from API",
    { recordCount: data.units_by_brand.length },
    "api",
  );
  return data;
}

/**
 * Fetch multi-retailer overview data from the backend.
 * No fallback - throws error if API fails.
 */
export async function fetchOverviewData(): Promise<MultiRetailerOverviewData> {
  const url = buildApiUrl("/overview");

  log.info("Fetching multi-retailer overview data from API", { url }, "api");
  const response = await fetch(url);

  if (!response.ok) {
    const errorDetails = getHttpStatusMessage(response);
    log.error(
      "Multi-retailer overview request failed",
      { status: response.status, errorDetails },
      "api",
    );
    throw new Error(errorDetails);
  }

  const data: MultiRetailerOverviewData = await response.json();

  log.info(
    "Multi-retailer overview data fetched successfully from API",
    { retailerCount: data.total_retailers },
    "api",
  );
  return data;
}

/**
 * Fetch retailer-specific dashboard data from the backend.
 * No fallback - throws error if API fails.
 */
export async function fetchRetailerData(
  retailerId: string,
): Promise<DashboardData> {
  const url = buildApiUrl(`/retailers/${retailerId}/dashboard`);

  log.info(
    "Fetching retailer dashboard data from API",
    { retailerId, url },
    "api",
  );
  const response = await fetch(url);

  if (!response.ok) {
    const errorDetails = getHttpStatusMessage(response);
    log.error(
      "Retailer dashboard request failed",
      { retailerId, status: response.status, errorDetails },
      "api",
    );
    throw new Error(errorDetails);
  }

  const data: DashboardData = await response.json();

  log.info(
    "Retailer dashboard data fetched successfully from API",
    { retailerId, recordCount: data.units_by_brand.length },
    "api",
  );
  return data;
}
